import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './BillSplitter.css';

const BillSplitter = ({ darkMode, toggleDarkMode }) => {
  const location = useLocation();
  const [names, setNames] = useState(['']);
  const [items, setItems] = useState([{ name: '', price: '', selected: [], subtotal: 0 }]);
  const [tax, setTax] = useState('');
  const [tip, setTip] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const inputRefs = useRef([]);

  // Generate order number starting at 0000000001 and incrementing (up to 1 billion)
  const [orderNumber] = useState(() => {
    const stored = localStorage.getItem('orderNumber');
    if (stored) {
      const nextOrder = (parseInt(stored, 10) + 1).toString().padStart(10, '0');
      localStorage.setItem('orderNumber', nextOrder);
      return nextOrder;
    }
    const firstOrder = '0000000001';
    localStorage.setItem('orderNumber', firstOrder);
    return firstOrder;
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const data = queryParams.get('data');
    if (data) {
      try {
        // Try to decode base64url first (new format)
        let decoded;
        try {
          const base64url = data.replace(/-/g, '+').replace(/_/g, '/');
          const jsonString = atob(base64url);
          decoded = JSON.parse(jsonString);
        } catch (e) {
          // Fallback to old format
          decoded = JSON.parse(decodeURIComponent(data));
        }

        // Handle optimized format (new)
        if (decoded.n !== undefined) {
          setNames(decoded.n.length > 0 ? decoded.n : ['']);
          setItems(decoded.i ? decoded.i.map(item => ({
            name: item.n || '',
            price: item.p ? item.p.toString() : '',
            selected: item.s || [],
            subtotal: 0
          })) : [{ name: '', price: '', selected: [], subtotal: 0 }]);
          setTax(decoded.t !== undefined ? decoded.t.toString() : '');
          setTip(decoded.tp !== undefined ? decoded.tp.toString() : '');
        } else {
          // Handle old format (backward compatibility)
          setNames(decoded.names || ['']);
          setItems(decoded.items || [{ name: '', price: '', selected: [], subtotal: 0 }]);
          setTax(decoded.tax || '');
          setTip(decoded.tip || '');
        }
      } catch (error) {
        console.error('Error parsing data:', error);
      }
    }
  }, []);

  const handleNameChange = (index, event) => {
    const newNames = [...names];
    newNames[index] = event.target.value;
    setNames(newNames);
  };

  const handleItemChange = (index, field, event) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: event.target.value };
    setItems(newItems);
  };

  const handleNameClick = (itemIndex, nameIndex) => {
    const newItems = items.map((item, idx) => {
      if (idx === itemIndex) {
        const selected = item.selected.includes(nameIndex)
          ? item.selected.filter(index => index !== nameIndex)
          : [...item.selected, nameIndex];

        const subtotal = calculateSubtotal(item.price, selected.length);
        return { ...item, selected, subtotal };
      }
      return item;
    });

    setItems(newItems);
  };

  const handleToggleAllClick = (itemIndex) => {
    const item = items[itemIndex];
    const allSelected = item.selected.length === names.length && names.length > 0;

    const selected = allSelected ? [] : names.map((_, index) => index);
    const subtotal = calculateSubtotal(item.price, selected.length);

    const newItems = items.map((item, idx) => {
      if (idx === itemIndex) {
        return { ...item, selected, subtotal };
      }
      return item;
    });

    setItems(newItems);
  };

  const calculateSubtotal = (price, numberOfSelected) => {
    const originalPrice = parseFloat(price);

    if (isNaN(originalPrice) || !isFinite(originalPrice) || originalPrice === 0) {
      return '-';
    }

    return numberOfSelected === 0 ? originalPrice : (originalPrice / numberOfSelected);
  };

  const handleAddName = () => {
    setNames([...names, '']);
    setTimeout(() => {
      inputRefs.current[inputRefs.current.length - 1]?.focus();
    }, 0);
  };

  const handleRemoveName = (index) => {
    const newNames = names.filter((_, i) => i !== index);
    setNames(newNames);
    const newItems = items.map(item => ({
      ...item,
      selected: item.selected.filter(sel => sel !== index).map(sel => sel > index ? sel - 1 : sel),
    }));
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([...items, { name: '', price: '', selected: [], subtotal: 0 }]);
    setTimeout(() => {
      inputRefs.current[inputRefs.current.length - 1]?.focus();
    }, 0);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  const handleClearAll = () => {
    setShowClearConfirm(true);
  };

  const confirmClearAll = () => {
    setNames(['']);
    setItems([{ name: '', price: '', selected: [], subtotal: 0 }]);
    setTax('');
    setTip('');
    setShowClearConfirm(false);
  };

  const cancelClearAll = () => {
    setShowClearConfirm(false);
  };

  const handleKeyPress = (event, action) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      action();
    }
  };

  const handleExport = () => {
    // Optimize data for shorter URLs
    const optimizedData = {
      n: names.filter(n => n && n.trim()), // Only non-empty names
      i: items
        .filter(item => item.price && parseFloat(item.price) > 0) // Only items with prices
        .map(item => {
          const itemData = {
            p: parseFloat(item.price) || 0, // Convert to number
            s: item.selected // Keep selected array
          };
          // Only include name if it's not empty
          if (item.name && item.name.trim()) {
            itemData.n = item.name.trim();
          }
          return itemData;
        }),
      t: tax && parseFloat(tax) > 0 ? parseFloat(tax) : undefined, // Only include if > 0
      tp: tip && parseFloat(tip) > 0 ? parseFloat(tip) : undefined // Only include if > 0
    };
    
    // Remove undefined values to further reduce size
    if (optimizedData.t === undefined) delete optimizedData.t;
    if (optimizedData.tp === undefined) delete optimizedData.tp;

    // Minify JSON
    const jsonString = JSON.stringify(optimizedData);
    
    // Encode to base64url for shorter URLs
    const base64 = btoa(jsonString);
    const base64url = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    
    const url = new URL(window.location.href);
    url.searchParams.set('data', base64url);
    
    navigator.clipboard.writeText(url.href).then(() => {
      alert('URL copied to clipboard!');
    }).catch((error) => {
      console.error('Failed to copy URL: ', error);
    });
  };

  const calculateTotals = () => {
    const subtotals = names.map((_, nameIndex) =>
      items.reduce((sum, item) => {
        if (item.selected.includes(nameIndex)) {
          const itemShare = parseFloat(item.price) / item.selected.length;
          return sum + itemShare;
        }
        return sum;
      }, 0)
    );

    const taxAmount = subtotals.map(subtotal => subtotal * (parseFloat(tax) / 100 || 0));
    const subtotalPlusTax = subtotals.map((subtotal, index) => subtotal + taxAmount[index]);

    const tipAmount = subtotalPlusTax.map(subtotal => subtotal * (parseFloat(tip) / 100 || 0));

    const totals = subtotals.map((subtotal, index) => subtotal + taxAmount[index] + tipAmount[index]);

    return { subtotals, taxAmount, tipAmount, totals };
  };

  const { subtotals, taxAmount, tipAmount, totals } = calculateTotals();

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  const billTotal = totals.reduce((sum, total) => sum + total, 0);

  return (
    <div className="bill-splitter">
      {/* Receipt Header */}
      <div className="receipt-header">
        <h1 className="receipt-title">ONECHECK</h1>
        
        {/* Navigation text links under title */}
        <div className="receipt-nav-under-title">
          <div className="receipt-nav-links-text">
            <Link 
              to="/" 
              className={`receipt-nav-link-text ${location.pathname === '/' ? 'active' : ''}`}
            >
              Home
            </Link>
            <span className="receipt-nav-separator">|</span>
            <Link 
              to="/about" 
              className={`receipt-nav-link-text ${location.pathname === '/about' ? 'active' : ''}`}
            >
              About
            </Link>
            <span className="receipt-nav-separator">|</span>
            <button 
              className="receipt-dark-toggle-text" 
              onClick={toggleDarkMode}
              aria-label="Toggle dark mode"
            >
              {darkMode ? 'Light Mode' : 'Dark Mode'}
            </button>
          </div>
        </div>
        
        <p className="receipt-subtitle">100% Free Online Check Splitter</p>
        <p className="receipt-date">{currentDate}</p>
        <p className="receipt-order-number">ORDER NUMBER {orderNumber}</p>
      </div>

      {/* Names Section */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">People</h2>
          <button className="btn-icon" onClick={handleAddName} aria-label="Add person">
            +
          </button>
        </div>
        <div className="names-grid">
          {names.map((name, index) => (
            <div key={index} className="name-item">
              <input
                type="text"
                className="input-field"
                placeholder={`Person ${index + 1}`}
                value={name}
                onChange={e => handleNameChange(index, e)}
                onKeyPress={e => handleKeyPress(e, handleAddName)}
                ref={el => inputRefs.current[index] = el}
              />
              <button 
                className="btn-remove" 
                onClick={() => handleRemoveName(index)}
                aria-label="Remove person"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Items Section */}
      <section className="section">
        <div className="section-header">
          <h2 className="section-title">Items</h2>
          <button className="btn-icon" onClick={handleAddItem} aria-label="Add item">
            +
          </button>
        </div>
        {items.map((item, itemIndex) => (
          <div key={itemIndex} className="item-row">
            <div className="item-inputs">
              <input
                type="text"
                className="input-field"
                placeholder="Item name (optional)"
                value={item.name}
                onChange={e => handleItemChange(itemIndex, 'name', e)}
                onKeyPress={e => handleKeyPress(e, handleAddItem)}
              />
              <div className="input-wrapper">
                <input
                  type="number"
                  className="input-field"
                  placeholder="Price"
                  value={item.price}
                  onChange={e => handleItemChange(itemIndex, 'price', e)}
                  onKeyPress={e => handleKeyPress(e, handleAddItem)}
                />
              </div>
            </div>
            <div className="item-actions">
              <div className="name-buttons">
                {names.map((name, nameIndex) => {
                  if (!name.trim()) return null;
                  return (
                    <button
                      key={nameIndex}
                      className={`name-btn ${item.selected.includes(nameIndex) ? 'selected' : ''}`}
                      onClick={() => handleNameClick(itemIndex, nameIndex)}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
              <div className="item-controls">
                <button 
                  className="btn-toggle" 
                  onClick={() => handleToggleAllClick(itemIndex)}
                >
                  Toggle All
                </button>
                <button 
                  className="btn-remove-text" 
                  onClick={() => handleRemoveItem(itemIndex)}
                >
                  Remove Item
                </button>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Tax and Tip Section */}
      <section className="section">
        <h2 className="section-title">Tax & Tip</h2>
        <div className="input-group">
          <div className="input-wrapper">
            <input
              type="number"
              className="input-field"
              placeholder="Tax %"
              value={tax}
              onChange={e => setTax(e.target.value)}
            />
            <span className="input-suffix">%</span>
          </div>
          <div className="input-wrapper">
            <input
              type="number"
              className="input-field"
              placeholder="Tip %"
              value={tip}
              onChange={e => setTip(e.target.value)}
            />
            <span className="input-suffix">%</span>
          </div>
        </div>
      </section>

      {/* Totals Section */}
      <section className="section">
        <h2 className="section-title">Totals</h2>
        <div className="totals-table-wrapper">
          <table className="totals-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Subtotal</th>
                <th>Tax</th>
                <th>Tip</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {names.map((name, index) => {
                if (!name.trim()) return null;
                return (
                  <tr key={index}>
                    <td>{name}</td>
                    <td>${subtotals[index].toFixed(2)}</td>
                    <td>${taxAmount[index].toFixed(2)}</td>
                    <td>${tipAmount[index].toFixed(2)}</td>
                    <td>${totals[index].toFixed(2)}</td>
                  </tr>
                );
              })}
              <tr className="bill-total-row">
                <td colSpan="3"></td>
                <td className="bill-total-label">Check Total:</td>
                <td className="bill-total-right">${billTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Action Buttons */}
      <div className="action-buttons-section">
        <div className="action-buttons">
          <button className="btn-primary btn-clear" onClick={handleClearAll}>
            Clear All
          </button>
          <button className="btn-primary btn-export" onClick={handleExport}>
            Export & Share
          </button>
        </div>
      </div>

      {/* Donation Footer */}
      <div className="donation-footer">
        <a href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" className="donation-link" target="_blank" rel="noopener noreferrer">Insert donation link here</a>
      </div>

      {/* Clear All Confirmation Modal */}
      {showClearConfirm && (
        <div className="modal-overlay" onClick={cancelClearAll}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Clear All Data?</h3>
            <p>Are you sure you want to clear all data? This action cannot be undone.</p>
            <div className="modal-buttons">
              <button className="btn-primary btn-clear" onClick={confirmClearAll}>
                Yes, Clear All
              </button>
              <button className="btn-primary" onClick={cancelClearAll}>
                No, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillSplitter;
